import { Graph, Node } from "@antv/x6";
import dagre from "dagre";
import { useEffect, useLayoutEffect, useRef } from "react";
import {
  CompanyTreeItem,
  NODE_COLORS,
  NODE_LABELS,
  TreeNodeType,
} from "./CompanyTree.default";

export interface CompanyTreeProps {
  readonly items: CompanyTreeItem[];
  readonly onExpandTenant: (tenantUid: string) => Promise<CompanyTreeItem[]>;
  readonly onExpandCompany: (companyUid: string) => Promise<CompanyTreeItem[]>;
  readonly onExpandDepartment: (
    departmentUid: string,
  ) => Promise<CompanyTreeItem[]>;
  readonly onExpandRole: (
    departmentUid: string,
    roleUid: string,
  ) => Promise<CompanyTreeItem[]>;
  /** Fires when a leaf user node is clicked — lets the parent surface a profile preview. */
  readonly onNodeClick?: (item: CompanyTreeItem) => void;
}

const NODE_WIDTH = 220;
const NODE_HEIGHT = 68;

// Perceptually-tuned lighter companion for each solid fill, used as the gradient highlight at the top
// of every card. Keeps the palette unified while giving each node a glossy sheen.
const NODE_HIGHLIGHTS: Record<TreeNodeType, string> = {
  tenant: "#B5B5B5",
  company: "#8FB5FF",
  department: "#6ED8D2",
  role: "#F3A369",
  employee: "#C58AD4",
  asset: "#4A6885",
};

function CompanyTree(props: CompanyTreeProps) {
  const ref = useRef<HTMLDivElement>(null!);
  const graphRef = useRef<Graph | null>(null);
  const expandedRef = useRef<Set<string>>(new Set());
  const childrenCacheRef = useRef<Map<string, CompanyTreeItem[]>>(new Map());
  const propsRef = useRef(props);
  propsRef.current = props;

  // Lifecycle

  useEffect(() => {
    if (graphRef.current) return;

    Graph.registerNode(
      "org-node",
      {
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        markup: [
          // Soft dark shadow behind the card. Rendered as a sibling rather than a filter so x6's
          // default rendering keeps rendering fast on large trees (hundreds of nodes).
          { tagName: "rect", selector: "shadow" },
          // The card body carries the solid tier colour.
          { tagName: "rect", selector: "body" },
          // Translucent highlight laid over the top half for a subtle glossy sheen.
          { tagName: "rect", selector: "sheen" },
          // Pill chip in the top-left spelling out what kind of node this is — a visual anchor the
          // tier colour alone can't carry (colour-blind safety plus just-in-case recognition).
          { tagName: "rect", selector: "typeChip" },
          { tagName: "text", selector: "typeChipText" },
          { tagName: "text", selector: "title" },
          { tagName: "text", selector: "subtitle" },
          { tagName: "rect", selector: "badge" },
          { tagName: "text", selector: "badgeText" },
        ],
        attrs: {
          shadow: {
            refX: 2,
            refY: 3,
            refWidth: "100%",
            refHeight: "100%",
            rx: 12,
            ry: 12,
            fill: "rgba(15, 23, 42, 0.18)",
            pointerEvents: "none",
          },
          body: {
            refWidth: "100%",
            refHeight: "100%",
            fill: "#5F95FF",
            stroke: "rgba(255,255,255,0.35)",
            strokeWidth: 1,
            rx: 12,
            ry: 12,
            pointerEvents: "visiblePainted",
            cursor: "default",
          },
          sheen: {
            refX: 1,
            refY: 1,
            refWidth: "100%",
            refWidth2: -2,
            height: NODE_HEIGHT / 2,
            rx: 11,
            ry: 11,
            fill: "rgba(255,255,255,0.18)",
            pointerEvents: "none",
          },
          typeChip: {
            refX: 10,
            refY: 9,
            width: 60,
            height: 15,
            rx: 7,
            ry: 7,
            fill: "rgba(255,255,255,0.22)",
            pointerEvents: "none",
          },
          typeChipText: {
            refX: 40,
            refY: 16.5,
            fill: "#fff",
            fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
            fontSize: 9,
            fontWeight: 700,
            textAnchor: "middle",
            textVerticalAnchor: "middle",
            letterSpacing: 0.6,
            pointerEvents: "none",
          },
          title: {
            refX: 12,
            refY: 38,
            fill: "#fff",
            fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
            fontSize: 13,
            fontWeight: "600",
            textAnchor: "start",
            textVerticalAnchor: "middle",
            pointerEvents: "none",
          },
          subtitle: {
            refX: 12,
            refY: 54,
            fill: "rgba(255,255,255,0.78)",
            fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
            fontSize: 10.5,
            textAnchor: "start",
            textVerticalAnchor: "middle",
            pointerEvents: "none",
          },
          badge: {
            width: 32,
            height: 20,
            refX: NODE_WIDTH - 42,
            refY: 8,
            rx: 10,
            ry: 10,
            fill: "#fff",
            stroke: "rgba(255,255,255,0.9)",
            strokeWidth: 0,
            visibility: "hidden",
            pointerEvents: "none",
          },
          badgeText: {
            refX: NODE_WIDTH - 26,
            refY: 18,
            fontSize: 11,
            fontWeight: "700",
            fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
            textAnchor: "middle",
            textVerticalAnchor: "middle",
            visibility: "hidden",
            pointerEvents: "none",
          },
        },
      },
      true,
    );

    Graph.registerEdge(
      "org-edge",
      {
        zIndex: -1,
        attrs: {
          line: {
            fill: "none",
            strokeLinejoin: "round",
            strokeWidth: 1.8,
            stroke: "#CBD5E1",
            sourceMarker: null,
            targetMarker: null,
          },
        },
        // Smooth curved connectors ("manhattan" + rounded corners feels more organic than straight lines).
        router: { name: "manhattan", args: { padding: 10 } },
        connector: { name: "rounded", args: { radius: 10 } },
      },
      true,
    );

    const graph = new Graph({
      container: ref.current,
      autoResize: true,
      interacting: false,
      panning: {
        enabled: true,
        eventTypes: ["leftMouseDown"],
      },
      mousewheel: {
        enabled: true,
        modifiers: [],
      },
      background: {
        // Soft off-white canvas; the dot-grid is layered on via CSS below.
        color: "#F8FAFC",
      },
      grid: {
        visible: true,
        type: "dot",
        size: 16,
        args: { color: "#CBD5E1", thickness: 1.2 },
      } as any,
    });
    graphRef.current = graph;

    if (propsRef.current.items.length > 0) {
      rebuildGraph(graph);
    }

    // Hover feedback — brightens the border and lifts the shadow slightly for depth.
    graph.on("node:mouseenter", ({ node }) => {
      node.attr("body/strokeWidth", 2.5);
      node.attr("body/stroke", "rgba(255,255,255,0.85)");
      node.attr("shadow/fill", "rgba(15,23,42,0.3)");
    });
    graph.on("node:mouseleave", ({ node }) => {
      node.attr("body/strokeWidth", 1);
      node.attr("body/stroke", "rgba(255,255,255,0.35)");
      node.attr("shadow/fill", "rgba(15,23,42,0.18)");
    });

    graph.on("node:click", async ({ node }) => {
      const data = node.getData() as {
        uid: string;
        nodeType: TreeNodeType;
        expandableCount: number;
        departmentUid?: string;
        roleUid?: string;
      };

      if (!data) return;
      // Leaf user nodes aren't expandable but the parent may want to open a profile preview.
      if (data.nodeType === "employee") {
        const item = findItem(data.uid);
        if (item) propsRef.current.onNodeClick?.(item);
        return;
      }
      if (data.expandableCount === 0) return;

      const isExpanded = expandedRef.current.has(data.uid);

      if (isExpanded) {
        expandedRef.current.delete(data.uid);
        removeDescendantsFromExpanded(data.uid);
        childrenCacheRef.current.delete(data.uid);
        rebuildGraph(graph);
      } else {
        try {
          let children: CompanyTreeItem[];
          if (data.nodeType === "tenant") {
            children = await propsRef.current.onExpandTenant(data.uid);
          } else if (data.nodeType === "company") {
            children = await propsRef.current.onExpandCompany(data.uid);
          } else if (data.nodeType === "department") {
            children = await propsRef.current.onExpandDepartment(data.uid);
          } else {
            children = await propsRef.current.onExpandRole(
              data.departmentUid!,
              data.roleUid!,
            );
          }
          childrenCacheRef.current.set(data.uid, children);
          expandedRef.current.add(data.uid);
          rebuildGraph(graph);
        } catch (err) {
          console.error("Failed to expand node:", err);
        }
      }
    });

    return () => {
      graphRef.current?.dispose();
      graphRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    if (graphRef.current && props.items.length > 0) {
      expandedRef.current.clear();
      childrenCacheRef.current.clear();
      rebuildGraph(graphRef.current);
    }
  }, [props.items]);

  // Methods

  function removeDescendantsFromExpanded(parentUid: string): void {
    const children = childrenCacheRef.current.get(parentUid);
    if (!children) return;

    for (const child of children) {
      if (expandedRef.current.has(child.uid)) {
        expandedRef.current.delete(child.uid);
        removeDescendantsFromExpanded(child.uid);
      }
      childrenCacheRef.current.delete(child.uid);
    }
  }

  function collectAllItems(): CompanyTreeItem[] {
    const all: CompanyTreeItem[] = [...propsRef.current.items];

    function appendChildren(parentUid: string): void {
      const children = childrenCacheRef.current.get(parentUid);
      if (!children) return;
      for (const child of children) {
        all.push(child);
        if (expandedRef.current.has(child.uid)) {
          appendChildren(child.uid);
        }
      }
    }

    for (const item of propsRef.current.items) {
      if (expandedRef.current.has(item.uid)) {
        appendChildren(item.uid);
      }
    }

    return all;
  }

  function rebuildGraph(graph: Graph): void {
    graph.clearCells();

    const allItems = collectAllItems();

    const g = new dagre.graphlib.Graph();
    g.setGraph({
      rankdir: "TB",
      nodesep: 40,
      ranksep: 80,
    });
    g.setDefaultEdgeLabel(() => ({}));

    const itemMap = new Map<string, CompanyTreeItem>();
    allItems.forEach((item) => {
      itemMap.set(item.uid, item);
      g.setNode(item.uid, { width: NODE_WIDTH, height: NODE_HEIGHT });
    });

    allItems.forEach((item) => {
      const parentId =
        item.nodeType === "company"
          ? (item.parentCompanyUid ?? item.parentUid)
          : item.parentUid;
      if (parentId && itemMap.has(parentId)) {
        g.setEdge(parentId, item.uid);
      }
    });

    dagre.layout(g);

    const nodeMap = new Map<string, Node>();
    allItems.forEach((item) => {
      const pos = g.node(item.uid);
      const isExpanded = expandedRef.current.has(item.uid);
      const node = addMember(
        graph,
        pos.x - NODE_WIDTH / 2,
        pos.y - NODE_HEIGHT / 2,
        item,
        isExpanded,
      );
      nodeMap.set(item.uid, node);
    });

    allItems.forEach((item) => {
      const parentId =
        item.nodeType === "company"
          ? (item.parentCompanyUid ?? item.parentUid)
          : item.parentUid;
      if (parentId && nodeMap.has(parentId)) {
        const source = nodeMap.get(parentId)!;
        const target = nodeMap.get(item.uid)!;
        linkMember(graph, source, target);
      }
    });

    graph.zoomToFit({ padding: 20, maxScale: 1 });
  }

  function addMember(
    graph: Graph,
    x: number,
    y: number,
    item: CompanyTreeItem,
    isExpanded: boolean,
  ) {
    const color = NODE_COLORS[item.nodeType];
    const highlight = NODE_HIGHLIGHTS[item.nodeType];
    const canExpand = item.expandableCount > 0;
    // Leaf user nodes click-through to the profile preview, so they get the pointer cursor too.
    const isClickable = canExpand || item.nodeType === "employee";
    const showBadge = canExpand;
    const badgeLabel = isExpanded ? "\u2212" : String(item.expandableCount);

    return graph.addNode({
      shape: "org-node",
      x,
      y,
      data: {
        uid: item.uid,
        nodeType: item.nodeType,
        expandableCount: item.expandableCount,
        departmentUid: item.departmentUid,
        roleUid: item.roleUid,
      },
      attrs: {
        body: {
          fill: color,
          cursor: isClickable ? "pointer" : "default",
        },
        sheen: { fill: `rgba(255,255,255,0.22)` },
        typeChip: { fill: highlight },
        typeChipText: {
          text: NODE_LABELS[item.nodeType].toUpperCase(),
        },
        title: { text: item.name },
        subtitle: { text: item.rank },
        badge: {
          visibility: showBadge ? "visible" : "hidden",
          fill: "#FFFFFF",
        },
        badgeText: {
          text: badgeLabel,
          fill: color,
          visibility: showBadge ? "visible" : "hidden",
        },
      },
    });
  }

  function findItem(uid: string): CompanyTreeItem | undefined {
    for (const item of propsRef.current.items) {
      if (item.uid === uid) return item;
    }
    const allChildren = Array.from(childrenCacheRef.current.values());
    for (const children of allChildren) {
      for (const child of children) {
        if (child.uid === uid) return child;
      }
    }
    return undefined;
  }

  function linkMember(graph: Graph, source: Node, target: Node) {
    return graph.addEdge({
      source: { cell: source },
      target: { cell: target },
      shape: "org-edge",
    } as any);
  }

  return (
    <div
      ref={ref}
      className="flex flex-1 flex-col"
      style={{
        width: "100%",
        height: "100%",
        borderRadius: 12,
        boxShadow: "inset 0 0 0 1px rgba(203, 213, 225, 0.5)",
        overflow: "hidden",
      }}
    />
  );
}

export default CompanyTree;
